import React, { useEffect, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import CampaignDetailsTab from './CampaignDetailsTab'
import api from 'api'
import FillingSpinner from 'components/FillingSpinner'
import './CampaignDetailsTabs.scss'
import { CampaignsByTagNode, GetCampaignForDetailsData } from '../../../api/campaign/types'
import Button, { ButtonSize, ButtonTheme } from 'components/Form/Button'
import { faShare } from '@fortawesome/free-solid-svg-icons'
import Modal from 'components/Modal'
import CampaignDetailsShareUrl from '../CampaignDetails/CampaignDetailsShareUrl'
import { Routes } from 'routes'

interface CampaignDetailsTabsProps {
  publicView?: boolean
}

const CampaignDetailsTabs: React.FC<CampaignDetailsTabsProps> = ({ publicView }) => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [campaignIds, setCampaignIds] = useState<string[]>([])
  const [activeTab, setActiveTab] = useState<string>('')
  const [tabNames, setTabNames] = useState<Map<string, string>>(new Map())
  const [campaignUuids, setCampaignUuids] = useState<Map<string, string>>(new Map())
  const [prefetchedCampaignDataMap, setPrefetchedCampaignDataMap] = useState<
    Map<string, GetCampaignForDetailsData>
  >(new Map())
  const [isShareUrlModalOpen, setIsShareUrlModalOpen] = useState<boolean>(false)

  const tag = searchParams.get('tag') || ''
  const location = useLocation()

  useEffect(() => {
    if (publicView) {
      const uuids = searchParams.getAll('uuid[]')
      if (uuids.length > 0) {
        setCampaignIds(uuids)
        if (!activeTab || !uuids.includes(activeTab)) {
          setActiveTab(uuids[0])
        }
      }
    } else {
      const ids = searchParams.getAll('id[]')
      if (ids.length > 0) {
        setCampaignIds(ids)
        if (!activeTab || !ids.includes(activeTab)) {
          setActiveTab(ids[0])
        }
      }
    }
  }, [searchParams])

  useEffect(() => {
    if (tag && !publicView) {
      const namesMap = new Map<string, string>()
      const state = location.state as CampaignsByTagNode[] | null
      if (!state || state.length === 0) {
        api.campaign
          .getCampaignsByTag(tag)
          .then(res => {
            const { nodes } = res.data.campaigns
            nodes.forEach(node => {
              namesMap.set(node.id, node.name)
              setCampaignUuids(prev => new Map(prev).set(node.id, node.uuid))
            })
            setTabNames(namesMap)
          })
          .catch(() => void {})
      } else {
        state.forEach(node => {
          namesMap.set(node.id, node.name)
          setCampaignUuids(prev => new Map(prev).set(node.id, node.uuid))
        })
        setTabNames(namesMap)
      }
    }
  }, [tag, location.state])

  useEffect(() => {
    if (publicView && campaignIds.length > 0) {
      campaignIds.forEach(uuid => {
        api.campaign
          .getCampaignForDetailsPublic(uuid)
          .then(res => {
            const campaignRawData = res.data.campaignReportPublic.campaign
            setTabNames(prev => new Map(prev).set(uuid, campaignRawData.name))
            setPrefetchedCampaignDataMap(prev => new Map(prev).set(uuid, campaignRawData))
          })
          .catch(() => void {})
      })
    }
  }, [publicView, campaignIds])

  const getTabLabel = (id: string) => {
    return tabNames.get(id) || ''
  }

  const buildShareUrl = (): string => {
    const params = new URLSearchParams()
    campaignIds.forEach(id => {
      const uuid = campaignUuids.get(id)
      if (uuid) {
        params.append('uuid[]', uuid)
      }
    })
    return `${window.location.origin}${Routes.CAMPAIGNS.COMPARE_PUBLIC}?${params.toString()}`
  }

  const allUuidsLoaded = campaignIds.length > 0 && campaignIds.every(id => campaignUuids.has(id))
  const allTabNamesLoaded = campaignIds.length > 0 && campaignIds.every(id => tabNames.has(id))

  if (campaignIds.length === 0) {
    return (
      <div className='CampaignDetailsTabs__empty'>{t('campaignDetails.noSelectedCampaigns')}</div>
    )
  }

  return (
    <div className='CampaignDetailsTabs'>
      <div className='CampaignDetailsTabs__header'>
        <div className='CampaignDetailsTabs__header-left'>
          {tag && (
            <>
              <h3>{t('common.tags')}:</h3> <span>{tag}</span>
            </>
          )}
        </div>
        {!publicView && (
          <div className='CampaignDetailsTabs__header-right'>
            <Button
              theme={ButtonTheme.NAVY_BLUE_OUTLINE}
              size={ButtonSize.SMALL}
              icon={faShare}
              disabled={!allUuidsLoaded}
              onClick={() => void setIsShareUrlModalOpen(true)}
            >
              {t('common.share')}
            </Button>
          </div>
        )}
      </div>
      {publicView && !allTabNamesLoaded ? (
        <FillingSpinner />
      ) : (
        <>
          <div className='CampaignDetailsTabs__tabs'>
            {campaignIds.map(id => (
              <button
                key={id}
                className={`CampaignDetailsTabs__tab ${
                  activeTab === id ? 'CampaignDetailsTabs__tab--active' : ''
                }`}
                onClick={() => setActiveTab(id)}
              >
                {getTabLabel(id)}
              </button>
            ))}
          </div>
          <div className='CampaignDetailsTabs__content'>
            {activeTab && (
              <CampaignDetailsTab
                key={activeTab}
                campaignId={publicView ? undefined : activeTab}
                campaignUuid={publicView ? activeTab : undefined}
                publicView={publicView}
                prefetchedCampaignData={
                  publicView ? prefetchedCampaignDataMap.get(activeTab) : undefined
                }
              />
            )}
          </div>
        </>
      )}

      {isShareUrlModalOpen && (
        <Modal
          title={t('campaignDetails.shareUrl.title')}
          isOpen={isShareUrlModalOpen}
          onClose={(isOpen: boolean) => void setIsShareUrlModalOpen(isOpen)}
          footer={
            <Button onClick={() => void setIsShareUrlModalOpen(false)}>
              {t('common.finished')}
            </Button>
          }
        >
          <CampaignDetailsShareUrl url={buildShareUrl()} />
        </Modal>
      )}
    </div>
  )
}

export default CampaignDetailsTabs
